export type UiFlagCategory = 'Cheating' | 'Safety' | 'Mental Health' | 'Explicit Content';
export type SafetyLabel = 'SAFETY' | 'NOT_SAFETY';
export type SafetyType = 'GENERAL_SAFETY' | 'MENTAL_HEALTH' | 'EXPLICIT_CONTENT';

export type FlagCategoryInput = {
  reason?: string | null;
  tags?: string[] | null;
  modCategories?: Record<string, unknown> | null;
  finalCategory?: Record<string, unknown> | null;
  parentLabel?: 'CHEATING' | 'NOT_CHEATING' | null;
  systemLabel?: 'CHEATING' | 'NOT_CHEATING' | null;
  finalLabel?: 'CHEATING' | 'NOT_CHEATING' | null;
  parentSafetyLabel?: SafetyLabel | null;
  systemSafetyLabel?: SafetyLabel | null;
  finalSafetyLabel?: SafetyLabel | null;
  parentSafetyType?: SafetyType | null;
  systemSafetyType?: SafetyType | null;
  finalSafetyType?: SafetyType | null;
};

export const MODERATION_SUBCATEGORY_LABELS: Record<string, string> = {
  harassment: 'Harassment',
  hate: 'Hate',
  'self-harm': 'Self-harm',
  'self-harm/intent': 'Self-harm intent',
  'self-harm/instructions': 'Self-harm instructions',
  sexual: 'Sexual content',
  'sexual/minors': 'Sexual content involving minors',
  violence: 'Violence',
  'violence/graphic': 'Graphic violence',
  illicit: 'Illegal or unsafe request',
  'illicit/violent': 'Violent illegal request',
  other: 'Other safety',
};

const CHEATING_REASONS = new Set(['anti_cheat', 'anti_cheat_error', 'integrity_high']);
const SAFETY_REASONS = new Set([
  'moderation_block',
  'embedding_match',
  'embedding_error',
  'safety_red',
  'safety_yellow',
  'router_review',
]);
const SELF_HARM_KEYS = new Set(['self-harm', 'self-harm/intent', 'self-harm/instructions']);
const SEXUAL_KEYS = new Set(['sexual', 'sexual/minors']);

const normalizeValue = (value?: string | null) => value?.trim().toLowerCase() ?? '';

const getEffectiveSafetyLabel = (flag: Pick<FlagCategoryInput, 'finalSafetyLabel' | 'parentSafetyLabel' | 'systemSafetyLabel'>): SafetyLabel | null =>
  flag.finalSafetyLabel ?? flag.parentSafetyLabel ?? flag.systemSafetyLabel ?? null;

const getEffectiveSafetyType = (flag: Pick<FlagCategoryInput, 'finalSafetyType' | 'parentSafetyType' | 'systemSafetyType'>): SafetyType | null =>
  flag.finalSafetyType ?? flag.parentSafetyType ?? flag.systemSafetyType ?? null;

const getEffectiveModerationCategories = (
  flag: Pick<FlagCategoryInput, 'modCategories' | 'finalCategory' | 'finalSafetyLabel' | 'parentSafetyLabel' | 'systemSafetyLabel'>,
): Record<string, unknown> | null => {
  const effectiveSafetyLabel = getEffectiveSafetyLabel(flag);
  if (effectiveSafetyLabel === 'NOT_SAFETY') return null;
  return flag.finalCategory ?? flag.modCategories ?? null;
};

const formatFallbackLabel = (value: string) =>
  value
    .replace(/\//g, ' / ')
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

export function getModerationSubcategoryLabel(value?: string | null): string | null {
  const normalized = normalizeValue(value);
  if (!normalized) return null;
  return MODERATION_SUBCATEGORY_LABELS[normalized] ?? formatFallbackLabel(normalized);
}

export function getFlagUiCategory(flag: FlagCategoryInput): UiFlagCategory {
  const tags = (flag.tags ?? []).map(normalizeValue).filter(Boolean);
  const modCategoryKeys = Object.entries(getEffectiveModerationCategories(flag) ?? {})
    .filter(([, enabled]) => Boolean(enabled))
    .map(([key]) => normalizeValue(key))
    .filter(Boolean);

  if (
    flag.parentLabel === 'CHEATING' ||
    flag.systemLabel === 'CHEATING' ||
    flag.finalLabel === 'CHEATING' ||
    tags.includes('cheating') ||
    CHEATING_REASONS.has(flag.reason ?? '')
  ) {
    return 'Cheating';
  }

  const safetyType = getEffectiveSafetyType(flag);
  if (safetyType === 'MENTAL_HEALTH') return 'Mental Health';
  if (safetyType === 'EXPLICIT_CONTENT') return 'Explicit Content';

  if (tags.some((tag) => SELF_HARM_KEYS.has(tag)) || modCategoryKeys.some((key) => SELF_HARM_KEYS.has(key))) {
    return 'Mental Health';
  }

  if (tags.some((tag) => SEXUAL_KEYS.has(tag)) || modCategoryKeys.some((key) => SEXUAL_KEYS.has(key))) {
    return 'Explicit Content';
  }

  return 'Safety';
}

export function getFlagModerationSubcategoryLabels(
  flag: Pick<
    FlagCategoryInput,
    'tags' | 'modCategories' | 'finalCategory' | 'finalSafetyLabel' | 'parentSafetyLabel' | 'systemSafetyLabel'
  >,
): string[] {
  const rawKeys = [
    ...Object.entries(getEffectiveModerationCategories(flag) ?? {})
      .filter(([, enabled]) => Boolean(enabled))
      .map(([key]) => key),
    ...((flag.tags ?? []).filter((tag) => normalizeValue(tag) !== 'cheating')),
  ];

  return rawKeys
    .map((key) => getModerationSubcategoryLabel(key))
    .filter((label, index, labels): label is string => Boolean(label) && labels.indexOf(label) === index);
}

export function getFlagReviewLabel(flag: FlagCategoryInput): string {
  if (
    flag.parentLabel === 'CHEATING' ||
    flag.systemLabel === 'CHEATING' ||
    flag.finalLabel === 'CHEATING'
  ) {
    return 'Cheating';
  }

  const safetyLabel = getEffectiveSafetyLabel(flag);
  if (safetyLabel === 'SAFETY') {
    return 'Safety';
  }

  const uiCategory = getFlagUiCategory(flag);
  if (uiCategory === 'Mental Health' || uiCategory === 'Explicit Content' || uiCategory === 'Safety') {
    return uiCategory;
  }

  if (safetyLabel === 'NOT_SAFETY') {
    const safetyType = getEffectiveSafetyType(flag);
    if (safetyType === 'MENTAL_HEALTH') return 'No mental-health concern';
    if (safetyType === 'EXPLICIT_CONTENT') return 'No explicit-content concern';
    return 'No safety issue';
  }

  const moderationLabels = getFlagModerationSubcategoryLabels(flag);
  if (moderationLabels.length > 0) {
    return moderationLabels[0];
  }

  if (SAFETY_REASONS.has(flag.reason ?? '')) {
    return 'Safety';
  }

  if (
    flag.parentLabel === 'NOT_CHEATING' ||
    flag.systemLabel === 'NOT_CHEATING' ||
    flag.finalLabel === 'NOT_CHEATING'
  ) {
    return 'Not cheating';
  }

  return getFlagUiCategory(flag);
}
